import { NotImplementedError } from "../../../../errors";
import { parseDateFilter } from "../../../../models/looker/modules/overview";
import { PromiseHandler } from "../../../../handlers";
import { OVERVIEW_SPEND_ANALYSIS_QUERY } from "./query";
/**
 * Daily spend (total_cost) over time for the Overview dashboard's Spend Analysis
 * widget. A single series: spend per date. Spend only — the widget reports spend
 * over time and does not follow the section's metric control, so there is no
 * metric to parameterize. Mirrors the Daily Impressions handler's filter/scope
 * discipline.
 */
export default class LookerOverviewSpendAnalysis extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "looker-overview-spend-analysis");
    }
    async findItems(filters) {
        const dateFilter = filters.where?.find((filter) => filter.field === "dates");
        if (!dateFilter) {
            throw new Error("Dates filter is required");
        }
        const queryBody = {
            ...OVERVIEW_SPEND_ANALYSIS_QUERY,
            filters: {
                "explore_overview.flt_date_range": parseDateFilter(dateFilter)
            }
        };
        const lookbackFilter = filters.where?.find((filter) => filter.field === "lookbackDays");
        if (lookbackFilter?.value !== undefined && lookbackFilter.value !== null) {
            queryBody.filters["explore_overview.pmt_lookback_window"] = String(lookbackFilter.value);
        }
        const mediaTypeFilter = filters.where?.find((filter) => filter.field === "mediaType");
        const mediaType = mediaTypeFilter?.value;
        if (mediaType && mediaType.length) {
            queryBody.filters["explore_overview.product_group"] = Array.isArray(mediaType)
                ? mediaType.join(",")
                : mediaType;
        }
        // Applied when present, matching the sibling daily-impressions and breakdown
        // handlers. These filters are what scope the query to the partner, and the
        // hook refuses to fetch without both, so an empty selection never reaches
        // here; the conditional only covers the handler being called directly.
        const advertiserFilter = filters.where?.find((filter) => filter.field === "advertiserIds");
        const advertiserIds = advertiserFilter?.value;
        if (advertiserIds?.length) {
            queryBody.filters["meta_inst.advertiser_id"] = advertiserIds.join(",");
        }
        const campaignFilter = filters.where?.find((filter) => filter.field === "campaignIds");
        const campaignIds = campaignFilter?.value;
        if (campaignIds?.length) {
            queryBody.filters["meta_inst.campaign_id"] = campaignIds.join(",");
        }
        const hashKey = await this.sdk.looker.query.hashQuery(queryBody);
        return this.cache.promise(hashKey, () => this.sdk.looker.query
            .run(queryBody)
            .then(({ data, queryMeta }) => [
            {
                data: this.formatResponse(data),
                queryMeta
            }
        ])
            .catch((e) => {
            throw Object.assign(new Error("Failed to fetch overview spend analysis"), {
                cause: e
            });
        }));
    }
    /**
     * Pivots Looker's long-format rows (one per date per product) into one row
     * per date keyed by product, which is what a multi-series chart consumes.
     * Spend is summed because the explore can split a date/product into more than
     * one row (device type, subtype). Rows missing a date or product are dropped
     * rather than bucketed under an empty key, which would draw an unnamed series.
     */
    formatResponse(data) {
        const byDate = new Map();
        data.forEach((row) => {
            const date = row["explore_overview.date_date"];
            const product = row["explore_overview.product_group"];
            if (!date || !product) {
                return;
            }
            const products = byDate.get(date) ?? {};
            products[product] =
                (products[product] ?? 0) + (Number(row["explore_overview.total_cost"]) || 0);
            byDate.set(date, products);
        });
        return [...byDate.entries()]
            .map(([date, byProduct]) => ({ date, byProduct }))
            .sort((a, b) => a.date.localeCompare(b.date));
    }
    async saveItem() {
        throw new NotImplementedError("saveItem");
    }
    async deleteItem() {
        throw new NotImplementedError("deleteItem");
    }
}
//# sourceMappingURL=SpendAnalysis.js.map